import Identity from './identity';
import { socialLogin } from './socialSignOn';
import getFacebookLoginStatus from './getFacebookLoginStatus';

import { isFacebookLoginResponse, FacebookLoginResponse } from './socialSignOnTypes';
import { APIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import { UserIdentity } from './userIdentity';

/**
 * Intended to be used as a callback (passed as `data-onlogin` or `onlogin`) to the "Facebook Login" button provided by Facebook.
 * Please refer to [Facebook's documentation](https://developers.facebook.com/docs/facebook-login/web/login-button) to get a Login Button.
 *
 * When used with the Facebook button, this function will prompt the user to login to Facebook if they have not already done so. If they have not previously logged in with Facebook in your application, a new account is created in your application and logged in. If they have previously logged in, the user is simply logged into your app.
 *
 * @returns
 * A __Promise__ resolving to a user's identity. When successful, updates Identity.userIdentity witih this information
 *
 */
export async function facebookSignOn(): Promise<UserIdentity | APIErrorResponse> {
  const facebookLoginStatus = await getFacebookLoginStatus(true);
  if (isFacebookLoginResponse(facebookLoginStatus)) {
    if (facebookLoginStatus.status === 'connected') {
      return await socialLogin('facebook', {
        credentials: facebookLoginStatus.authResponse.accessToken
      });
    } else if (Identity._useCustomFBButton) {
      return customButtonFacebookSignOn();
    } else {
      throw new Error(
        `User canceled login, did not fully authorize, or is not logged in to Facebook: ${facebookLoginStatus}`
      );
    }
  } else {
    throw new Error(`Error getting login status. Please try again. ${facebookLoginStatus}`);
  }
}

/**
 * @hidden
 */
export async function customButtonFacebookSignOn(): Promise<UserIdentity | APIErrorResponse> {
  return window.FB.login(async (response: FacebookLoginResponse) => {
    if (response.status === 'connected') {
      return await socialLogin('facebook', {
        credentials: response.authResponse.accessToken
      });
    } else {
      throw new Error(
        `User canceled login, did not fully authorize, or is not logged in to Facebook: ${response}`
      );
    }
  });
}

export default facebookSignOn;
